import type {
  CreatorCollaborationPackage,
  CreatorPlacement
} from '@/biz/multiPlatformCollaboration/model';
import type { InfluencerAccount } from '@/apis/influencerAccounts';
import {
  getCreatorContentTypeKey,
  getCreatorPlacementAvailableContentTypes,
  getNewCreatorChannelContentTypes
} from './contentTypes';

export type CreatorAccountPickerAccount = {
  id: string;
  platform: string;
  userName: string;
  avatar?: string;
  placementIds: string[];
  verified: boolean;
  verifyHref: string;
  initiallySelected?: boolean;
  disabledReason?: 'REMOVED' | 'BOUND_BY_OTHER' | 'UNSUPPORTED';
};

export type CreatorAccountPickerOwner = {
  id: string;
  name: string;
  avatar?: string;
  avatarBackground?: string;
  accounts: CreatorAccountPickerAccount[];
};

export type CreatorAccountPickerPlatformAccount = {
  id: string;
  owner: CreatorAccountPickerOwner;
  account: CreatorAccountPickerAccount;
};

export type CreatorAccountPickerPlatform = {
  id: string;
  platform: string;
  accounts: CreatorAccountPickerPlatformAccount[];
};

const PLATFORM_ORDER = [
  'youtube',
  'tiktok',
  'instagram',
  'twitter',
  'linkedin',
  'facebook'
];

const firstText = (...values: unknown[]) => {
  const value = values.find(
    (item) => item !== undefined && item !== null && String(item).trim() !== ''
  );
  return value === undefined ? '' : String(value);
};

const getPlacementAvatar = (placement: CreatorPlacement) =>
  firstText(
    placement.raw?.avatar,
    placement.raw?.avatarUrl,
    placement.raw?.profileImage,
    placement.raw?.influencer?.avatar,
    placement.raw?.user?.imageUrl
  );

const UNVERIFIED_ACCOUNT_STATUSES = new Set([
  'bound_by_other',
  'expired',
  'locked_by_other',
  'locked_by_me',
  'mounted_unverified',
  'removed',
  'unsupported',
  'unverified'
]);

const isAccountVerified = (account: Record<string, any>) => {
  const status = firstText(
    account?.verificationStatus,
    account?.accountStatus,
    account?.status
  ).toLowerCase();
  if (UNVERIFIED_ACCOUNT_STATUSES.has(status)) return false;
  if (status === 'bound_by_me') return true;
  if (typeof account?.verified === 'boolean') return account.verified;
  if (typeof account?.isVerified === 'boolean') return account.isVerified;
  if (typeof account?.isAuthorized === 'boolean') return account.isAuthorized;
  return true;
};

const getExplicitOwnerRecords = (
  collaborationPackage: CreatorCollaborationPackage
): Record<string, any>[] => {
  const raw = collaborationPackage.raw ?? {};
  const container =
    raw.multiPlatformCollaboration ??
    raw.collaborationPackage ??
    raw.orderPackage ??
    {};
  const records =
    raw.accountPickerOwners ??
    raw.availableAccountOwners ??
    container.accountPickerOwners ??
    container.availableAccountOwners;
  if (Array.isArray(records)) return records;

  return [];
};

const normalizeExplicitOwners = (
  collaborationPackage: CreatorCollaborationPackage,
  records: Record<string, any>[]
): CreatorAccountPickerOwner[] => {
  const validPlacementIds = new Set(
    collaborationPackage.placements.map(({ id }) => id)
  );

  return records.flatMap((owner, ownerIndex) => {
    const ownerId = firstText(
      owner?.id,
      owner?._id,
      owner?.userId,
      `account-owner-${ownerIndex}`
    );
    const ownerName = firstText(
      owner?.name,
      owner?.displayName,
      owner?.userName,
      owner?.username,
      'Creator'
    );
    const rawAccounts = owner?.accounts ?? owner?.channels;
    if (!Array.isArray(rawAccounts) || rawAccounts.length === 0) return [];

    const accounts = rawAccounts.flatMap(
      (account: Record<string, any>, accountIndex: number) => {
        const platform = firstText(account?.platform).toLowerCase();
        if (!platform) return [];
        const requestedPlacementIds = Array.isArray(account?.placementIds)
          ? account.placementIds
          : [account?.placementId ?? account?.orderId].filter(Boolean);
        const placementIds = requestedPlacementIds
          .map(String)
          .filter((id) => validPlacementIds.has(id));
        return [
          {
            id: firstText(
              account?.id,
              account?._id,
              `${ownerId}-${platform}-${accountIndex}`
            ),
            platform,
            userName: firstText(
              account?.userName,
              account?.username,
              account?.handle,
              ownerName
            ).replace(/^@/, ''),
            avatar: firstText(account?.avatar, account?.avatarUrl) || undefined,
            placementIds,
            verified: isAccountVerified(account),
            verifyHref: firstText(
              account?.verifyHref,
              '/settings#connectSocials'
            ),
            initiallySelected:
              typeof account?.selected === 'boolean'
                ? account.selected
                : typeof account?.included === 'boolean'
                  ? account.included
                  : undefined,
            disabledReason: account?.disabledReason
          }
        ];
      }
    );

    return accounts.length
      ? [
          {
            id: ownerId,
            name: ownerName,
            avatar:
              firstText(owner?.avatar, owner?.avatarUrl, owner?.imageUrl) ||
              undefined,
            avatarBackground:
              firstText(owner?.avatarBackground, owner?.avatarColor) ||
              undefined,
            accounts
          }
        ]
      : [];
  });
};

const buildFallbackOwners = (
  collaborationPackage: CreatorCollaborationPackage
): CreatorAccountPickerOwner[] => {
  const owners = new Map<string, CreatorAccountPickerOwner>();

  collaborationPackage.placements.forEach((placement) => {
    const raw = placement.raw ?? {};
    const ownerId = firstText(
      raw.accountPickerOwnerId,
      raw.accountOwnerId,
      raw.ownerId,
      raw.user?.id,
      raw.influencer?.id,
      placement.userName,
      'current-creator'
    );
    const ownerName = firstText(
      raw.accountPickerOwnerName,
      raw.accountOwnerName,
      raw.ownerName,
      raw.user?.name,
      raw.influencer?.name,
      placement.userName,
      'Creator'
    );
    const owner = owners.get(ownerId) ?? {
      id: ownerId,
      name: ownerName,
      avatar: getPlacementAvatar(placement) || undefined,
      accounts: []
    };
    const userName = firstText(placement.userName, ownerName).replace(/^@/, '');
    const accountId = `${ownerId}:${placement.platform}:${userName}`;
    const existingAccount = owner.accounts.find(
      (account) => account.id === accountId
    );

    if (existingAccount) {
      existingAccount.placementIds.push(placement.id);
    } else {
      owner.accounts.push({
        id: accountId,
        platform: placement.platform,
        userName,
        avatar: getPlacementAvatar(placement) || undefined,
        placementIds: [placement.id],
        verified: isAccountVerified(raw),
        verifyHref: '/settings#connectSocials'
      });
    }
    owners.set(ownerId, owner);
  });

  return Array.from(owners.values());
};

export const getCreatorAccountPickerOwners = (
  collaborationPackage: CreatorCollaborationPackage
): CreatorAccountPickerOwner[] => {
  const explicitRecords = getExplicitOwnerRecords(collaborationPackage);
  const explicitOwners = normalizeExplicitOwners(
    collaborationPackage,
    explicitRecords
  );
  if (Array.isArray(collaborationPackage.raw?.accountPickerOwners)) {
    return explicitOwners;
  }
  return explicitOwners.length
    ? explicitOwners
    : buildFallbackOwners(collaborationPackage);
};

export const getCreatorAccountPickerPlatforms = (
  owners: CreatorAccountPickerOwner[]
): CreatorAccountPickerPlatform[] => {
  const platforms = new Map<string, CreatorAccountPickerPlatform>();
  const accountSortRank = (account: CreatorAccountPickerAccount) =>
    account.verified ? 0 : 1;
  const nameCollator = new Intl.Collator('en', {
    sensitivity: 'base',
    numeric: true
  });

  owners.forEach((owner) => {
    owner.accounts.forEach((account) => {
      const platform = account.platform.toLowerCase();
      const group = platforms.get(platform) ?? {
        id: platform,
        platform,
        accounts: []
      };
      group.accounts.push({
        id: `${owner.id}:${account.id}`,
        owner,
        account
      });
      platforms.set(platform, group);
    });
  });

  platforms.forEach((platform) => {
    platform.accounts.sort((a, b) => {
      const rankDifference =
        accountSortRank(a.account) - accountSortRank(b.account);
      if (rankDifference) return rankDifference;
      return (
        nameCollator.compare(
          a.account.userName || a.owner.name,
          b.account.userName || b.owner.name
        ) || nameCollator.compare(a.id, b.id)
      );
    });
  });

  return Array.from(platforms.values()).sort((a, b) => {
    const aIndex = PLATFORM_ORDER.indexOf(a.platform);
    const bIndex = PLATFORM_ORDER.indexOf(b.platform);
    if (aIndex === -1 && bIndex === -1) {
      return a.platform.localeCompare(b.platform);
    }
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

/** Existing order channels use exactly the same type options as the detail page. */
export const withCreatorOrderContentTypes = (
  collaborationPackage: CreatorCollaborationPackage
): CreatorCollaborationPackage => ({
  ...collaborationPackage,
  placements: collaborationPackage.placements.map((placement) => {
    const allowedContentTypes =
      getCreatorPlacementAvailableContentTypes(placement);
    const currentKey = getCreatorContentTypeKey(
      placement.platform,
      placement.contentType
    );
    return {
      ...placement,
      allowedContentTypes,
      contentType:
        allowedContentTypes.find(
          (type) =>
            getCreatorContentTypeKey(placement.platform, type) === currentKey
        ) ??
        allowedContentTypes[0] ??
        placement.contentType
    };
  })
});

/** Settings supplies account identity/binding; campaign supplies new channel type options. */
export const withCreatorInfluencerAccounts = (
  collaborationPackage: CreatorCollaborationPackage,
  influencerAccounts: InfluencerAccount[]
): CreatorCollaborationPackage => {
  const orderPackage = withCreatorOrderContentTypes(collaborationPackage);
  const seen = new Set<string>();
  const placements = [...orderPackage.placements];
  const orderIds = new Set(
    collaborationPackage.placements
      .filter((placement) => placement.raw?.active !== false)
      .map(({ id }) => id)
  );
  // Whether a new account can be added is still owned by quote capabilities.
  const accountPickerOwners = influencerAccounts.flatMap((account, index) => {
    const platform = firstText(account.platform).trim().toLowerCase();
    if (!platform) return [];
    const platformId = firstText(account.platformId);
    const channelKey = firstText(
      account.channelKey,
      platformId ? `${platform}:${platformId}` : ''
    );
    // Never match accounts by display name, array position or another account on the platform.
    const matched = orderPackage.placements.find(
      (placement) =>
        placement.platform.toLowerCase() === platform &&
        (account.channelKey
          ? placement.channelKey === account.channelKey
          : platformId
            ? String(placement.platformId ?? '') === platformId
            : account.influencerId != null &&
              String(placement.raw?.influencerId ?? '') ===
                String(account.influencerId))
    );
    const id =
      matched?.id ||
      channelKey ||
      `${platform}:${platformId || firstText(account.influencerId, index)}`;
    if (seen.has(id)) return [];
    seen.add(id);
    const userName = firstText(
      account.username,
      account.name,
      matched?.userName,
      platformId,
      'Creator'
    ).replace(/^@+/, '');
    const status = firstText(account.status).trim().toLowerCase();
    const verified = status === 'bound_by_me';
    const allowedContentTypes = matched
      ? getCreatorPlacementAvailableContentTypes(matched)
      : getNewCreatorChannelContentTypes(
          platform,
          collaborationPackage.raw?.campaign?.placements
        );
    const currentContentType = matched?.contentType;
    const placement: CreatorPlacement = {
      ...matched,
      id,
      platform,
      channelKey: matched?.channelKey || channelKey || undefined,
      platformId: matched?.platformId || platformId || undefined,
      userName,
      contentType: firstText(currentContentType, allowedContentTypes?.[0]),
      allowedContentTypes,
      raw: {
        ...matched?.raw,
        ...account,
        active: matched?.raw?.active !== false,
        disabledReason: undefined
      }
    };
    if (matched)
      placements[placements.findIndex((item) => item.id === matched.id)] =
        placement;
    else placements.push(placement);
    return [
      {
        id,
        name: userName,
        avatar: firstText(account.profilePictureLink) || undefined,
        accounts: [
          {
            id,
            platform,
            userName,
            avatar: firstText(account.profilePictureLink) || undefined,
            placementIds: [id],
            selected: orderIds.has(id),
            verified,
            verifyHref: '/settings#connectSocials'
          }
        ]
      }
    ];
  });
  return {
    ...orderPackage,
    placements,
    raw: { ...orderPackage.raw, accountPickerOwners }
  };
};

export const getCreatorAccountPickerInitialPlacementIds = (
  collaborationPackage: CreatorCollaborationPackage
) => {
  const channels = collaborationPackage.raw?.channels;
  if (Array.isArray(channels)) {
    const existingKeys = new Set(
      channels
        .filter((channel) => channel.active !== false)
        .map((channel) => channel.channelKey)
    );
    return collaborationPackage.placements
      .filter(
        (placement) =>
          placement.raw?.active !== false &&
          existingKeys.has(placement.channelKey || placement.id)
      )
      .map(({ id }) => id);
  }
  const accounts = getCreatorAccountPickerOwners(collaborationPackage).flatMap(
    (owner) => owner.accounts
  );
  const hasExplicitInitialSelection = accounts.some(
    ({ initiallySelected }) => initiallySelected !== undefined
  );

  if (!hasExplicitInitialSelection) {
    return collaborationPackage.placements
      .filter((placement) => placement.raw?.active !== false)
      .map(({ id }) => id);
  }

  const selectedIds = new Set(
    accounts
      .filter(({ initiallySelected }) => initiallySelected)
      .flatMap(({ placementIds }) => placementIds)
  );
  return collaborationPackage.placements
    .map(({ id }) => id)
    .filter((id) => selectedIds.has(id));
};

export const getCreatorAccountSelectionState = (
  account: CreatorAccountPickerAccount,
  includedPlacementIds: ReadonlySet<string>
) => {
  const selectedCount = account.placementIds.filter((id) =>
    includedPlacementIds.has(id)
  ).length;
  return {
    selected: account.placementIds.length > 0 && selectedCount > 0,
    checked:
      account.placementIds.length > 0 &&
      selectedCount === account.placementIds.length,
    indeterminate:
      selectedCount > 0 && selectedCount < account.placementIds.length
  };
};

/** Account ownership is not permission to add that account to this order. */
export const getCreatorQuoteSelectablePlacementIds = (
  collaborationPackage: CreatorCollaborationPackage
) => {
  const channels = collaborationPackage.raw?.channels;
  const existingKeys = new Set<string>(
    Array.isArray(channels)
      ? channels
          .filter((channel) => channel.active !== false)
          .map((channel) => channel.channelKey)
      : []
  );
  return collaborationPackage.placements
    .filter((placement) => {
      const key = placement.channelKey || placement.id;
      return (
        placement.raw?.active !== false &&
        (!Array.isArray(channels) ||
          collaborationPackage.raw?.quoteCapabilities?.canAddChannels ===
            true ||
          existingKeys.has(key))
      );
    })
    .map(({ id }) => id);
};

export const toggleCreatorAccountPlacementIds = ({
  account,
  includedPlacementIds,
  placementOrder
}: {
  account: CreatorAccountPickerAccount;
  includedPlacementIds: string[];
  placementOrder: string[];
}) => {
  if (!account.verified) {
    return placementOrder.filter((id) => new Set(includedPlacementIds).has(id));
  }
  const next = new Set(includedPlacementIds);
  const { checked } = getCreatorAccountSelectionState(account, next);
  account.placementIds.forEach((id) => {
    if (checked) next.delete(id);
    else next.add(id);
  });
  return placementOrder.filter((id) => next.has(id));
};

export const toggleCreatorAccountPlatformPlacementIds = ({
  platform,
  includedPlacementIds,
  placementOrder
}: {
  platform: CreatorAccountPickerPlatform;
  includedPlacementIds: string[];
  placementOrder: string[];
}) => {
  const next = new Set(includedPlacementIds);
  const selectableAccounts = platform.accounts
    .map(({ account }) => account)
    .filter(({ verified, placementIds }) => verified && placementIds.length);
  const everyAccountSelected =
    selectableAccounts.length > 0 &&
    selectableAccounts.every(
      (account) => getCreatorAccountSelectionState(account, next).checked
    );

  selectableAccounts.forEach((account) => {
    account.placementIds.forEach((id) => {
      if (everyAccountSelected) next.delete(id);
      else next.add(id);
    });
  });
  return placementOrder.filter((id) => next.has(id));
};
